import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/configureStore';
import { showAdvisorReferralModalSelector } from 'store/selectors/modal';
import AvatarCircle from 'app/components/AvatarCircle/AvatarCircle';
import {
  actionHandleShowAdvisorReferralModal,
  actionHandleShowInfoModal,
} from 'store/reducers/modal';
import useTenantTranslation from 'utils/hooks/useTenantTranslation';
import ProfileFieldWrapper from 'app/components/ProfileFields/common/ProfileFieldWrapper';
import DropDownList from 'app/components/DropDownList/DropDownList';
import Textarea from 'app/components/Textarea/Textarea';
import { useParams } from 'react-router-dom';
import advisorService from '_services/advisorService';
import {
  AdvisorReferral,
  AdvisorReferralStatus,
} from '_types/advisor.interface';
import { profileAdvisorSelector } from 'store/selectors/profile';
import { creatAdvisorReferral } from 'store/actions/advisor';
import CongratulationsIcon from 'app/components/Icons/CongratulationsIcon';
import useTenantColors from 'utils/hooks/useTenantColors';
import { AxiosError } from 'axios';
import ErrorsText from 'app/components/ErrorsText';
import BaseModal from '../BaseModal';
import './style.scss';

type OrgOption = { name: string; value: string };
type AdvisorOption = { name: string; value: string };

const ADVISOR_LIMIT = 20;

const AdvisorReferralModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTenantTranslation();
  const [organization, setOrganization] = useState('All');
  const [advisor, setAdvisor] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<OrgOption[]>([
    { name: 'All', value: 'All' },
  ]);
  const [advisorsByOrg, setAdvisorsByOrg] = useState<
    Record<string, AdvisorOption[]>
  >({});
  const [advisorOffsetByOrg, setAdvisorOffsetByOrg] = useState<
    Record<string, number>
  >({});
  const [advisorHasMoreByOrg, setAdvisorHasMoreByOrg] = useState<
    Record<string, boolean>
  >({});
  const [isLoadingAdvisors, setIsLoadingAdvisors] = useState(false);

  const advisorData = useAppSelector(profileAdvisorSelector);
  const sboAsClient = useAppSelector(showAdvisorReferralModalSelector);
  const { colorVariables } = useTenantColors();
  const params = useParams();

  // Fetch orgs and first page of advisors for each org
  useEffect(() => {
    const fetchData = async () => {
      const orgList = await advisorService.getAdvisoryOrgsList();
      const advisorsByOrgTemp: Record<string, AdvisorOption[]> = {};
      const offsetByOrgTemp: Record<string, number> = {};
      const hasMoreByOrgTemp: Record<string, boolean> = {};
      const filteredOrgs: OrgOption[] = [];

      await Promise.all(
        orgList.map(async (org: any) => {
          const advisorsRaw = await advisorService
            .getAdvisorListByOrgId(org.id, 0, ADVISOR_LIMIT)
            .then((data) => data.advisorCards || []);
          if (advisorsRaw && advisorsRaw.length > 0) {
            const filteredAdvisors = advisorsRaw
              .filter((a) => a.isActive !== false)
              .map((a) => ({
                name: a.firstName,
                value: a.id.toString(),
              }));
            advisorsByOrgTemp[org.id] = filteredAdvisors;
            offsetByOrgTemp[org.id] = advisorsRaw.length;
            hasMoreByOrgTemp[org.id] = advisorsRaw.length === ADVISOR_LIMIT;
            if (filteredAdvisors.length > 0) {
              filteredOrgs.push({ name: org.name, value: org.id });
            }
          }
        }),
      );

      setOrgs([{ name: 'All', value: 'All' }, ...filteredOrgs]);
      setAdvisorsByOrg(advisorsByOrgTemp);
      setAdvisorOffsetByOrg(offsetByOrgTemp);
      setAdvisorHasMoreByOrg(hasMoreByOrgTemp);
    };

    fetchData();
  }, []);

  // Memoize available advisors based on selected org
  const availableAdvisors = useMemo(() => {
    if (organization === 'All') {
      return Object.values(advisorsByOrg).flat();
    }
    return advisorsByOrg[organization] || [];
  }, [organization, advisorsByOrg]);

  const closeModal = useCallback(() => {
    dispatch(actionHandleShowAdvisorReferralModal(null));
  }, [dispatch]);

  // Fetch advisors for a given org and offset, append to state
  const fetchMoreAdvisors = useCallback(
    async (orgId: string) => {
      if (isLoadingAdvisors || !advisorHasMoreByOrg[orgId]) {
        return;
      }
      setIsLoadingAdvisors(true);
      const offset = advisorOffsetByOrg[orgId] || 0;
      const advisors = await advisorService
        .getAdvisorListByOrgId(Number(orgId), offset, ADVISOR_LIMIT)
        .then((data) => data.advisorCards || []);
      setAdvisorsByOrg((prev) => {
        const filteredAdvisors = advisors
          .filter((a) => a.isActive !== false)
          .map((a) => ({
            name: a.firstName,
            value: a.id.toString(),
          }));
        return {
          ...prev,
          [orgId]: [...(prev[orgId] || []), ...filteredAdvisors],
        };
      });
      setAdvisorOffsetByOrg((prev) => ({
        ...prev,
        [orgId]: offset + advisors.length,
      }));
      setAdvisorHasMoreByOrg((prev) => ({
        ...prev,
        [orgId]: advisors.length === ADVISOR_LIMIT,
      }));
      setIsLoadingAdvisors(false);
    },
    [isLoadingAdvisors, advisorHasMoreByOrg, advisorOffsetByOrg],
  );

  // When org changes, reset advisor list and fetch first page
  const onSelectAdvisoryOrg = useCallback(
    (value: string) => {
      setOrganization(value);
      setAdvisor('');
      // If we don't have advisors for this org yet, fetch them
      if (!advisorsByOrg[value]) {
        fetchMoreAdvisors(value);
      }
    },
    [advisorsByOrg, fetchMoreAdvisors],
  );

  // Infinite scroll handler for advisor dropdown
  const handleAdvisorDropdownScrollEnd = useCallback(() => {
    if (organization === 'All') return;
    fetchMoreAdvisors(organization);
  }, [organization, fetchMoreAdvisors]);

  const referAdvisor = useCallback(async () => {
    const wildcardPath = params['*'];
    const id = wildcardPath?.split('/')[1];

    const advisorReferralData = {
      refereeAdvisorId: advisorData.id,
      referredAdvisorId: Number(advisor),
      sboId: Number(id),
      note,
      status: AdvisorReferralStatus.PENDING,
    } as AdvisorReferral;

    const result = await dispatch(creatAdvisorReferral(advisorReferralData));

    if (creatAdvisorReferral.fulfilled.match(result)) {
      closeModal();
      dispatch(
        actionHandleShowInfoModal({
          closeOnBackgroundClick: true,
          headerIcon: (
            <CongratulationsIcon
              color={colorVariables?.text.text_accent_1}
              className="icon"
            />
          ),
          modalTitle: t('common.modal.advisor_referral.success.title'),
          content: t('common.modal.advisor_referral.success.content'),
          closeButtonText: t(
            'common.modal.advisor_referral.success.close_button',
          ),
        }),
      );
    } else if (creatAdvisorReferral.rejected.match(result)) {
      const error = result.payload as AxiosError;
      const responseData = error.response?.data as { message: string };
      setError(responseData?.message);
      setTimeout(() => {
        const errorElement = document.querySelector('.errors_text');
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [
    note,
    advisorData,
    advisor,
    params,
    dispatch,
    colorVariables,
    t,
    closeModal,
  ]);

  const isDisableAcceptButton = useMemo(
    () => !(note && advisor && advisorData.id),
    [note, advisor, advisorData],
  );

  const headerIcon = useMemo(
    () => (
      <AvatarCircle
        firstName={sboAsClient?.businessOwnerProfile?.Profile?.Name as string}
        lastName={sboAsClient?.businessOwnerProfile?.Profile?.Surname as string}
        src={sboAsClient?.businessOwnerProfile?.Profile?.photo as string}
        size={{ small: '0', large: '86' }}
      />
    ),
    [sboAsClient],
  );

  return (
    <BaseModal
      headerIcon={headerIcon}
      modalTitle={t('common.modal.advisor_referral.title')}
      isCloseIconVisible
      closeOnBackgroundClick
      onModalClose={closeModal}
      withDeclineButton
      onDeclineButtonClick={closeModal}
      declineButtonText={t('common.cancel')}
      withAcceptButton
      onAcceptButtonClick={referAdvisor}
      acceptButtonText={t('common.modal.advisor_referral.accept_button')}
      disableAcceptButton={isDisableAcceptButton}
      classNames={{
        modal: 'advisor_referral_modal',
        modalTitle: 'advisor_referral_modal__title',
        modalHeader: 'advisor_referral_modal__header',
        modalInnerContainer: 'advisor_referral_modal__inner_container',
        acceptButton: 'modal-footer-buttons__acceptButton',
        declineButton: 'modal-footer-buttons__declineButton',
        closeIcon: 'advisor_referral_modal__closeIcon',
        modalFooter: 'advisor_referral_modal__footer',
      }}
    >
      {error && <ErrorsText errorsListOrErrorText={error} />}
      <ProfileFieldWrapper
        title={t('common.modal.advisor_referral.questions.organizations.title')}
        subTitle={t(
          'common.modal.advisor_referral.questions.organizations.description',
        )}
      >
        <DropDownList
          placeholder=""
          value={organization}
          onChange={onSelectAdvisoryOrg}
          availableValuesList={orgs}
          isArrow
        />
      </ProfileFieldWrapper>
      <ProfileFieldWrapper
        title={t('common.modal.advisor_referral.questions.advisor.title')}
        subTitle={t(
          'common.modal.advisor_referral.questions.advisor.description',
        )}
        addAsteriskToTitle
      >
        <DropDownList
          placeholder={t(
            'common.modal.advisor_referral.questions.advisor.placeholder',
          )}
          value={advisor}
          onChange={setAdvisor}
          availableValuesList={availableAdvisors}
          isArrow
          onScrollToEnd={handleAdvisorDropdownScrollEnd}
        />
      </ProfileFieldWrapper>
      <ProfileFieldWrapper
        title={t('common.modal.advisor_referral.questions.note.title')}
        subTitle={t('common.modal.advisor_referral.questions.note.description')}
        addAsteriskToTitle
      >
        <Textarea
          type="text"
          placeholder={t(
            'common.modal.advisor_referral.questions.note.placeholder',
          )}
          value={note}
          onChange={setNote}
          maxLength={200}
        />
      </ProfileFieldWrapper>
    </BaseModal>
  );
};

export default memo(AdvisorReferralModal);
